import { Component } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { distinctUntilChanged, startWith } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { DividerComponent, HeadingComponent } from '@core/user-interface-legacy';
import {
  canActivateDataSource,
  SimpleViewComponent,
  ViewsLocalStorageService,
} from '@core/views-legacy';
import type { DataSource, ViewForm, ViewFormHook } from '@core/views-types';
import { BoardGroupHeaderComponent, WrappedTableListGroupHeadingComponent } from '@core/views-ui';
import {
  dataSourceProviders,
  provideBoardLayout,
  provideTableLayout,
  provideViewBaseRoute,
} from '@core/views-util';

import { AskTicketBoardItemComponent } from './ask-ticket-board-item/ask-ticket-board-item.component';
import { AskTicketsDataSourceService } from './ask-tickets-data-source.service';
import { assignedToColumn } from './columns/assigned-to/assigned-to-column';
import { employeeColumn } from './columns/employee-column';
import { priorityColumn } from './columns/priority-column';
import { requestNameColumn } from './columns/request-name-column';
import { submittedAtColumn } from './columns/submitted-at-column';
import { askColumnFieldMap } from './columns/types';

const DATASOURCE_ID = 'ask_tickets';

@Component({
  selector: 'tlc-asks-list',
  template: `
    <div padding>
      <div layout layout-space-between layout-align-items-center>
        <div>
          <rt-heading variant="large heading">
            {{ 'tlc.ask_hr.requests.heading' | translate }}
          </rt-heading>
          <p>
            {{ 'tlc.ask_hr.requests.subheading' | translate }}
          </p>
        </div>
      </div>
    </div>
    <rt-divider margin-bottom />
    @if (canActivate()) {
      <rt-simple-view
        class="view"
        viewId="ask_tickets:all"
        [layouts]="['table', 'board']"
        [viewFormHook]="viewFormHook"
        [hasQuickFilters]="true"
        [hasGroupsControl]="false"
        [hasLayoutPicker]="true"
        [selfScrollEnabled]="true"
      />
    }
  `,
  imports: [SimpleViewComponent, DividerComponent, HeadingComponent, TranslatePipe],
  providers: [
    ViewsLocalStorageService,
    provideViewBaseRoute('/asks/list'),
    AskTicketsDataSourceService,
    dataSourceProviders(DATASOURCE_ID, {
      [DATASOURCE_ID]: AskTicketsDataSourceService,
    }),
    provideTableLayout({
      [DATASOURCE_ID]: {
        tableGroupHeader: WrappedTableListGroupHeadingComponent,
        columns: [
          requestNameColumn,
          priorityColumn,
          assignedToColumn,
          submittedAtColumn,
          employeeColumn,
        ],
        columnFieldMap: askColumnFieldMap,
      },
    }),
    provideBoardLayout({
      [DATASOURCE_ID]: {
        groupHeader: BoardGroupHeaderComponent,
        item: AskTicketBoardItemComponent,
      },
    }),
  ],
  styleUrls: ['./view-table-overrides.less', './ask-tickets-view.component.less'],
})
export class TellentAskTicketsViewComponent {
  private readonly canActivateFn = canActivateDataSource();
  private readonly canActivate$ = this.canActivateFn();
  protected canActivate = toSignal(this.canActivate$);

  protected viewFormHook: ViewFormHook = (viewForm: ViewForm, dataSource: DataSource) => {
    const { type: layoutTypeCtrl } = viewForm.controls.layout.controls;
    const sub = layoutTypeCtrl.valueChanges
      .pipe(startWith(layoutTypeCtrl.value), distinctUntilChanged())
      .subscribe(layoutType => {
        if (layoutType === 'table') {
          viewForm.controls.group.controls.configId.setValue(dataSource.groups[0].id);
        }
      });

    return () => sub.unsubscribe();
  };
}
